<template>
  <div class="iv-control-panel">
    <md-switch
      class="md-primary ui-theme-3"
      :value="iv.dropSet.enabled"
      :disabled="readonly"
      @change="updateDropSetEnabled"
    />
    <label>
      Drip Set
      <help-tooltip v-if="dropSetOrPumpError" type="error">
        {{ dropSetOrPumpError.message }}
      </help-tooltip>
    </label>
    <div class="emtpy-column" />
    <div v-show="iv.dropSet.enabled" class="range-options">
      <label class="range-label">Range</label>
      <vue-slider
        class="slider ui-theme-3"
        :min="0"
        :max="1000"
        :interval="10"
        :dot-size="35"
        :height="6"
        :contained="true"
        :lazy="true"
        :enable-cross="false"
        :dot-style="{backgroundColor: '#0C70B0'}"
        :value="[ iv.dropSet.minGTTM, iv.dropSet.maxGTTM ]"
        :disabled="readonly"
        @change="updateRange"
      />
      <label class="gttm-label">GTTM</label>
    </div>
    <div v-show="iv.dropSet.enabled" class="drip-set-options">
      <label class="drip-set-label">Drip Set</label>
      <radio-buttons
        bordered pill-shaped multiple
        active-color="blue-7"
        inactive-color="none"
        :options="dripSetOptions"
        :value="iv.dropSet.dripSet"
        :disabled="readonly"
        @input="sets => updateDripSet(sets)"
      />
      <help-tooltip v-if="dripSetError" type="error">
        {{ dripSetError.message }}
      </help-tooltip>
    </div>
    <md-switch
      class="md-primary ui-theme-3 row-3"
      :value="iv.pump.enabled"
      :disabled="readonly"
      @change="updatePumpEnabled"
    />
    <label class="row-3">
      IV Pump
      <help-tooltip v-if="dropSetOrPumpError" type="error">
        {{ dropSetOrPumpError.message }}
      </help-tooltip>
    </label>
    <div class="emtpy-column" />
    <div v-show="iv.pump.enabled" class="pump-options row-3">
      <md-switch
        class="md-primary ui-theme-3"
        :value="iv.pump.allowTitrate"
        :disabled="readonly"
        @change="updateAllowTitrate"
      />
      <label class="titrate-label">Allow Titrate</label>
      <md-switch
        class="md-primary ui-theme-3"
        :value="iv.pump.allowOptions"
        :disabled="readonly"
        @change="updateAllowOptions"
      />
      <label class="options-label">Allow primary/secondary options</label>
    </div>
  </div>
</template>

<script>
import { merge, get } from 'lodash';
import VueSlider from 'vue-slider-component';

import iv from '~lib/common/config/iv';
import HelpTooltip from '~lib/common/components/HelpTooltip';
import RadioButtons from '~lib/common/components/RadioButtons';

export default {
  name: 'iv-control-panel',
  components: {
    VueSlider,
    HelpTooltip,
    RadioButtons,
  },
  props: {
    iv: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    dripSetOptions() {
      return iv.tubings
        .map(tubing => ({
          label: tubing.label.en,
          value: tubing.key,
        }));
    },
    dropSetOrPumpError() {
      const errors = this.$store ? get(this.$store.getters['validation/errorsByFieldAndId'], 'iv.dropset-or-pump', []) : [];
      return errors.length > 0 ? errors[0] : null;
    },
    dripSetError() {
      const errors = this.$store ? get(this.$store.getters['validation/errorsByFieldAndId'], 'iv.dripset', []) : [];
      return errors.length > 0 ? errors[0] : null;
    },
  },
  methods: {
    updateDropSetEnabled(enabled) {
      const newIv = merge({}, this.iv, { dropSet: { enabled }});
      this.emitUpdate(newIv);
    },
    updateRange(range) {
      const newIv = merge({}, this.iv, { dropSet: { minGTTM: range[0], maxGTTM: range[1] } });
      this.emitUpdate(newIv);
    },
    updateDripSet(values) {
      const newIv = {
        ...this.iv,
        dropSet: {
          ...this.iv.dropSet,
          dripSet: values
        }
      }
      this.emitUpdate(newIv);
    },
    updatePumpEnabled(enabled) {
      const newIv = merge({}, this.iv, { pump: { enabled } });
      this.emitUpdate(newIv);
    },
    updateAllowTitrate(allow) {
      const newIv = merge({}, this.iv, { pump: { allowTitrate: allow } });
      this.emitUpdate(newIv);
    },
    updateAllowOptions(allow) {
      const newIv = merge({}, this.iv, { pump: { allowOptions: allow } });
      this.emitUpdate(newIv);
    },
    emitUpdate(iv) {
      this.$emit('updateIv', iv);
    }
  }
}
</script>

<style scoped>
.iv-control-panel {
  display: grid;
  grid-template-rows: repeat(2, 1fr);
  grid-template-columns: auto auto 100px auto auto auto;
  grid-gap: 1rem;
  align-items: center;
}

.range-options,
.drip-set-options {
  display: flex;
  align-items: center;
  grid-column-start: 4;
  grid-column-end: 6;
}

.pump-options {
  display: flex;
  align-items: center;
  grid-column-start: 4;
  grid-column-end: 6;
}

.row-3 {
  grid-row-start: 3;
}

.range-label,
.drip-set-label {
  margin-right: 1rem;
}

.gttm-label,
.titrate-label,
.options-label {
  margin-left: 1rem;
}

.titrate-label {
  margin-right: 2rem;
}

.slider {
  flex: 1;
  min-width: 400px;
}

.md-switch {
  margin: 0;
}
</style>